import { useCallback } from 'react';

import analyticsProvider from 'state/analytics';
import { setDatadogFlagsIdentity } from 'state/datadog-flags';
import { useLDContext } from 'state/launchdarkly';

import { UserDetails } from './types';

export const useThirdPartyAuthentication = () => {
  const { login: launchDarklyLogin, logout: launchDarklyLogout } = useLDContext();

  const logUserInToThirdPartyServices = useCallback(
    (currentUser: UserDetails) => {
      launchDarklyLogin(currentUser);

      // Without this the flag context keeps whoever the guest was when the
      // page loaded, so a guest who signs in mid-session stays bucketed as
      // signed-out until a full reload. The loyalty id goes with it because
      // that, not the account id, is what the app buckets rollouts on.
      setDatadogFlagsIdentity({
        cognitoId: currentUser.cognitoId,
        legacyCognitoId: currentUser.thLegacyCognitoId,
      });

      analyticsProvider.setCustomerIdentity({
        customerId: currentUser.thLegacyCognitoId
          ? `us-east-1:${currentUser.thLegacyCognitoId}`
          : currentUser.cognitoId,
        cognitoId: currentUser.cognitoId,
        userAttributes: currentUser.details,
      });
    },
    [launchDarklyLogin]
  );

  const logUserOutOfThirdPartyServices = useCallback(() => {
    launchDarklyLogout();

    // Back to the browser-scoped anonymous identity. The stored anonymous id
    // survives sign-out deliberately, so this re-points evaluation without
    // re-bucketing the guest.
    setDatadogFlagsIdentity();

    analyticsProvider.logOutCustomer();
  }, [launchDarklyLogout]);

  return { logUserInToThirdPartyServices, logUserOutOfThirdPartyServices };
};
